/**
 * Customer combobox for the invoice editor (design C, Customer card).
 *
 * Searches the shop's clients through the existing ClientRepository, like the
 * Customers screen. A picked client is sent as `{ clientId }` and the server
 * snapshots the record. "+ New customer" does NOT create a client record:
 * the shared client schema has no Company, TIN or VAT fields yet, so the
 * inline form fills the invoice's own customer snapshot instead.
 *
 * Outstanding balance per customer is not shown yet: there is no endpoint
 * that returns it per client (CONTRACT §4.4 customers route is a later phase).
 */
import React, { useEffect, useRef, useState } from 'react';
import ClientRepository from '../../../internals/repository/ClientRepository';
import type { EditorCustomer } from '../editorModel';

export interface ClientRecord {
  id: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  mobileNumber?: string;
  email?: string;
  address?: string;
  companyName?: string;
  tin?: string;
  vatRegistered?: boolean;
}

export const clientName = (c: ClientRecord) =>
  (c.name || [c.firstName, c.lastName].filter(Boolean).join(' ')).trim();

const phoneOf = (c: ClientRecord) => (c.mobileNumber && c.mobileNumber.length > 7 ? c.mobileNumber : '');

export function customerFromClient(c: ClientRecord): EditorCustomer {
  return {
    source: 'client',
    clientId: c.id,
    name: clientName(c),
    companyName: c.companyName || '',
    phone: phoneOf(c),
    email: c.email || '',
    address: c.address || '',
    tin: c.tin || '',
    vatRegistered: c.vatRegistered === true,
  };
}

const initials = (s: string) =>
  (s || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

interface NewCust { name: string; companyName: string; phone: string; email: string; tin: string; vatRegistered: boolean; tried: boolean }

export default function CustomerPicker({
  shopId, value, onChange, error, disabled,
}: {
  shopId?: string;
  value: EditorCustomer | null;
  onChange: (c: EditorCustomer | null) => void;
  error?: string;
  disabled?: boolean;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<ClientRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const [nc, setNc] = useState<NewCust | null>(null);
  const reqId = useRef(0);
  const listId = 'customer-picker-list';

  useEffect(() => {
    if (!open || !shopId) return undefined;
    const id = ++reqId.current;
    setLoading(true);
    const t = setTimeout(async () => {
      const text = q.trim();
      const res: any = text
        ? await ClientRepository.searchClients(shopId, text, 20, null)
        : await ClientRepository.fetchClients(shopId, 20, null);
      if (id !== reqId.current) return;
      const list = Array.isArray(res) ? res : Array.isArray(res?.items) ? res.items : [];
      setResults(list.filter((c: ClientRecord) => c && c.id).slice(0, 8));
      setActive(-1);
      setLoading(false);
    }, searchDelay(q));
    return () => clearTimeout(t);
  }, [q, open, shopId]);

  const pick = (c: ClientRecord) => {
    onChange(customerFromClient(c));
    setOpen(false);
    setQ('');
  };

  const startNew = () => {
    const t = q.trim();
    const isPhone = /^[0-9 +]+$/.test(t);
    setNc({ name: isPhone ? '' : t, companyName: '', phone: isPhone ? t : '', email: '', tin: '', vatRegistered: false, tried: false });
    setOpen(false);
  };

  const saveNew = () => {
    if (!nc) return;
    if (!nc.name.trim()) { setNc({ ...nc, tried: true }); return; }
    onChange({
      source: 'snapshot', clientId: null, name: nc.name.trim(), companyName: nc.companyName.trim(),
      phone: nc.phone.trim(), email: nc.email.trim(), address: '', tin: nc.tin.trim(), vatRegistered: nc.vatRegistered,
    });
    setNc(null);
    setQ('');
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const count = results.length + 1; // + New customer
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => (a + 1) % count); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a <= 0 ? count - 1 : a - 1)); }
    else if (e.key === 'Enter' && open && active >= 0) {
      e.preventDefault();
      if (active < results.length) pick(results[active]); else startNew();
    } else if (e.key === 'Escape') setOpen(false);
  };

  if (value) {
    const main = value.companyName || value.name || 'Customer';
    const line = [
      value.companyName ? value.name : '', value.phone, value.email,
      value.tin ? `TIN ${value.tin}` : '', value.vatRegistered ? 'VAT registered' : '',
    ].filter(Boolean).join(' · ');
    return (
      <div className="flex items-center gap-3 rounded-xl bg-surface2 px-3.5 py-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand-soft text-sm font-bold text-brand">
          {initials(main)}
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-semibold text-text1">{main}</span>
          {line && <span className="truncate text-[13px] text-text2">{line}</span>}
          {value.source === 'snapshot' && !value.clientId && (
            <span className="text-xs text-text3">On this invoice only. Not added to Customers.</span>
          )}
        </div>
        {!disabled && (
          <button type="button" className="nl-btn nl-btn--ghost nl-btn--sm" onClick={() => onChange(null)}>
            Change
          </button>
        )}
      </div>
    );
  }

  if (nc) {
    const field = (label: string, key: keyof NewCust, ph: string, mono = false, required = false) => {
      const bad = required && nc.tried && !String(nc[key]).trim();
      return (
        <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-text2">
          {label}{required && <span className="sr-only"> (required)</span>}
          <input
            className={`nl-input ${mono ? 'font-mono' : ''} ${bad ? '!border-neg' : ''}`}
            value={String(nc[key])}
            placeholder={ph}
            aria-invalid={bad || undefined}
            onChange={(e) => setNc({ ...nc, [key]: e.target.value })}
          />
          {bad && <span className="text-xs font-semibold text-[color:var(--negFg)]">Enter a name.</span>}
        </label>
      );
    };
    return (
      <div className="grid grid-cols-1 gap-3 rounded-xl border border-borderc bg-surface2 p-4 sm:grid-cols-2">
        {field('Name', 'name', 'Full name', false, true)}
        {field('Company', 'companyName', 'Optional')}
        {field('Phone', 'phone', '07X XXX XXXX', true)}
        {field('Email', 'email', 'name@company.lk')}
        {field('TIN', 'tin', 'Optional', true)}
        <label className="flex items-center gap-2 self-end pb-2.5 text-sm font-semibold text-text1">
          <input type="checkbox" className="nl-check" checked={nc.vatRegistered} onChange={() => setNc({ ...nc, vatRegistered: !nc.vatRegistered })} />
          VAT registered
        </label>
        <p className="text-xs text-text3 sm:col-span-2">
          Saved on this invoice only. Add the customer under Customers to reuse their details.
        </p>
        <div className="flex justify-end gap-2 sm:col-span-2">
          <button type="button" className="nl-btn nl-btn--secondary nl-btn--sm" onClick={() => setNc(null)}>Cancel</button>
          <button type="button" className="nl-btn nl-btn--primary nl-btn--sm" onClick={saveNew}>Use customer</button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label="Search customers"
        aria-invalid={!!error || undefined}
        aria-activedescendant={open && active >= 0 ? `cust-opt-${active}` : undefined}
        className={`nl-input ${error ? '!border-neg' : ''}`}
        placeholder="Search by name, company or phone"
        autoComplete="off"
        disabled={disabled}
        value={q}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={onKey}
      />
      {open && (
        <div
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+6px)] z-30 flex max-h-80 flex-col gap-0.5 overflow-auto rounded-xl border border-borderc bg-surface p-1.5 shadow-[var(--shadowLg)]"
        >
          {loading && !results.length && <span className="px-2.5 py-2 text-[13px] text-text3">Searching…</span>}
          {results.map((c, i) => {
            const name = clientName(c);
            const main = c.companyName || name || 'Unnamed customer';
            const sub = [c.companyName ? name : '', phoneOf(c)].filter(Boolean).join(' · ');
            return (
              <button
                key={c.id}
                id={`cust-opt-${i}`}
                type="button"
                role="option"
                aria-selected={active === i}
                onMouseDown={(e) => { e.preventDefault(); pick(c); }}
                className={`flex flex-col rounded-[7px] px-2.5 py-2 text-left hover:bg-surface2 ${active === i ? 'bg-surface2' : ''}`}
              >
                <span className="text-sm font-semibold text-text1">{main}</span>
                {sub && <span className="font-mono text-xs text-text3">{sub}</span>}
              </button>
            );
          })}
          {!loading && !results.length && <span className="px-2.5 py-2 text-[13px] text-text3">No customer matches.</span>}
          <div className="my-1 h-px bg-borderc" />
          <button
            id={`cust-opt-${results.length}`}
            type="button"
            role="option"
            aria-selected={active === results.length}
            onMouseDown={(e) => { e.preventDefault(); startNew(); }}
            className={`rounded-[7px] px-2.5 py-2 text-left text-sm font-semibold text-brand hover:bg-surface2 ${active === results.length ? 'bg-surface2' : ''}`}
          >
            + New customer{q.trim() ? ` “${q.trim()}”` : ''}
          </button>
        </div>
      )}
      {error && <span className="mt-1.5 block text-xs font-semibold text-[color:var(--negFg)]">! {error}</span>}
    </div>
  );
}

/** Debounce typing, but open the empty list at once. */
function searchDelay(q: string) {
  return q.trim() ? 250 : 0;
}
